import { useEffect, useCallback, useRef } from 'react';
import { parseSRT, parseVTT } from '../utils/captionParser';
import type { PlayerAction } from '../playerReducer';
import type { TrackInfo, CaptionCue } from '../types';

export function useCaptions(
  mediaEl: HTMLMediaElement | null,
  dispatch: React.Dispatch<PlayerAction>,
  selectedTrackIdx: number,
  captions: CaptionCue[],
  currentTime: number,
  fps?: number,
) {
  const lastCueTextRef = useRef('');
  // Discover tracks from the video element
  useEffect(() => {
    if (!mediaEl) return;

    const discoverTracks = () => {
      const tracks: TrackInfo[] = [];
      const trackEls = mediaEl.querySelectorAll('track');
      trackEls.forEach((t) => {
        tracks.push({
          label: t.label || t.srclang || 'Unknown',
          srclang: t.srclang || '',
          src: t.src,
          kind: t.kind || 'subtitles',
        });
        // Disable native rendering so we can render our own overlay
        t.track.mode = 'disabled';
      });
      dispatch({ type: 'SET_CAPTION_TRACKS', tracks });

      if (tracks.length === 0) {
        dispatch({ type: 'SET_ACTIVE_CUE', text: '' });
        lastCueTextRef.current = '';
      }
    };

    discoverTracks();

    // Also listen for dynamically added tracks
    const observer = new MutationObserver(discoverTracks);
    observer.observe(mediaEl, { childList: true });

    return () => observer.disconnect();
  }, [mediaEl, dispatch]);

  // Load and parse the selected track
  const loadTrack = useCallback(
    async (track: TrackInfo) => {
      try {
        const response = await fetch(track.src);
        const text = await response.text();

        let cues: CaptionCue[];
        if (track.src.endsWith('.srt')) {
          cues = parseSRT(text);
        } else {
          cues = parseVTT(text);
        }

        dispatch({ type: 'SET_CAPTIONS', captions: cues });
      } catch {
        dispatch({ type: 'SET_CAPTIONS', captions: [] });
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (selectedTrackIdx < 0) {
      dispatch({ type: 'SET_CAPTIONS', captions: [] });
      dispatch({ type: 'SET_ACTIVE_CUE', text: '' });
      lastCueTextRef.current = '';
      return;
    }
  }, [selectedTrackIdx, dispatch]);

  // Update active cue based on currentTime — only dispatch when
  // the text actually changes to avoid unnecessary re-renders
  useEffect(() => {
    if (captions.length === 0) {
      return;
    }

    // Match cues against the *frame* containing currentTime, not the raw
    // currentTime value. shuttle.seekTo snaps the requested time to the
    // middle of the containing frame ((floor(t*fps) + 0.5)/fps), which can
    // push currentTime past a cue's end when the cue ends within that
    // half-frame window — the caption then briefly disappears even though
    // the seek targeted that cue. Comparing the cue against the displayed
    // frame's full [frameStart, frameEnd) range handles both:
    //   - back-to-back cues at a boundary (cueA.end === cueB.start): the
    //     later cue wins because cueA.end > frameStart is false at frame N
    //     when boundary === N/fps
    //   - cues ending mid-frame after a seek snap: the cue still overlaps
    //     the frame range and matches
    // When fps is unavailable (audio without a known frame rate) fall back
    // to a half-open [start, end) point check.
    const activeCue = fps && fps > 0
      ? (() => {
          const frame = Math.floor(currentTime * fps + 1e-6);
          const frameStart = frame / fps;
          const frameEnd = (frame + 1) / fps;
          return captions.find(
            (cue) => cue.start < frameEnd && cue.end > frameStart,
          );
        })()
      : captions.find(
          (cue) => currentTime >= cue.start && currentTime < cue.end,
        );

    const text = activeCue ? activeCue.content : '';
    if (text !== lastCueTextRef.current) {
      lastCueTextRef.current = text;
      dispatch({ type: 'SET_ACTIVE_CUE', text, end: activeCue?.end ?? 0 });
    }
  }, [currentTime, captions, dispatch]);

  return { loadTrack };
}
